import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import { ActionFlowRequestSearch } from "@/shared/api/model";
import { useQuery } from '@tanstack/react-query';
import { getMyRequests } from "@/shared/api/client/action-flow";
import dayjs from 'dayjs';
import createParamsSerializer from '@/shared/utils/create-params-serializer';

const { Title } = Typography;
const { RangePicker } = DatePicker;

export const MyPendingRequestsView: React.FC = () => {
    const [form] = Form.useForm();
    const [searchParams, setSearchParams] = useState<ActionFlowRequestSearch>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    // Fetch only pending requests belonging to the current user
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['myPendingRequests', searchParams],
        queryFn: async () => {
            const response = await getMyRequests(
                // @ts-ignore - bypassing type check as backend expects flattened params
                {
                    ...searchParams,
                    status: 'PENDING'
                } as any,
                {
                    paramsSerializer: createParamsSerializer()
                }
            );
            return response.data;
        }
    });

    const requests = Array.isArray(data) ? data : [];

    const handleSearch = (values: any) => {
        const { dateRange, ...rest } = values;
        
        const newParams: ActionFlowRequestSearch = {
            ...searchParams,
            ...rest,
            from: dateRange ? dayjs(dateRange[0]).startOf('day').toDate() : undefined,
            to: dateRange ? dayjs(dateRange[1]).endOf('day').toDate() : undefined
        };
        
        setSearchParams(newParams);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({
            numberOfRows: 40,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const renderSearchForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            className="mb-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Form.Item name="q" label="Search">
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search records..."
                        allowClear
                    />
                </Form.Item>

                <Form.Item name="domain" label="Domain">
                    <Select
                        placeholder="Select domain"
                        allowClear
                        options={[
                            { label: 'Loans', value: 'LOANS' },
                            { label: 'Accounts', value: 'ACCOUNTS' },
                            { label: 'Customers', value: 'CUSTOMERS' }
                        ]}
                    />
                </Form.Item>

                <Form.Item name="dateRange" label="Date Range">
                    <RangePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label=" " className="flex justify-end">
                    <Space>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<FilterOutlined />}
                        >
                            Filter
                        </Button>
                    </Space>
                </Form.Item>
            </div>
        </Form>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>My Pending Requests</Title>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetch()}
                    loading={isLoading}
                >
                    Refresh
                </Button>
            </div>

            <Card>
                {renderSearchForm()}

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error}
                    showResendAction={false}
                    // @ts-ignore
                    hideSubmissionStep={true}
                    // @ts-ignore
                    searchParams={undefined}
                    // @ts-ignore
                    onParamsChange={function (params: ActionFlowRequestSearch): void {
                        throw new Error('Function not implemented.');
                    }}
                />
            </Card>

            {/* Help Section */}
            <Card title="Help & Information" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Title level={4}>About My Pending Requests</Title>
                        <ul className="list-disc pl-4">
                            <li>This page shows only your pending requests that are awaiting approval</li>
                            <li>You can track the status of your submitted requests</li>
                            <li>Use filters to narrow down your search</li>
                            <li>Click on any request to view full details</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>Search Tips</Title>
                        <ul className="list-disc pl-4">
                            <li>Use the search box to find specific records by name</li>
                            <li>Filter by date range to view requests from a specific period</li>
                            <li>Select domain to focus on specific request types</li>
                            <li>Use Reset to clear all filters and view all your pending requests</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default MyPendingRequestsView;
